import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Radio, RefreshCw, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const networkColors = {
  TRC20: "bg-[#26A17B]/15 text-[#26A17B] border-[#26A17B]/30",
  TRX: "bg-[#FF060A]/15 text-[#FF060A] border-[#FF060A]/30",
  BNB: "bg-[#F0B90B]/15 text-[#F0B90B] border-[#F0B90B]/30",
  BEP20: "bg-[#F0B90B]/15 text-[#F0B90B] border-[#F0B90B]/30",
  TON: "bg-[#0098EA]/15 text-[#0098EA] border-[#0098EA]/30",
};

const shorten = (s) => (s && s.length > 14 ? `${s.slice(0, 6)}…${s.slice(-6)}` : s || "—");

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const explorerUrl = (network, txid) => {
  if (!txid) return null;
  if (network === "TRC20" || network === "TRX") return `https://tronscan.org/#/transaction/${txid}`;
  if (network === "BEP20" || network === "BNB") return `https://bscscan.com/tx/${txid}`;
  if (network === "TON") return `https://tonviewer.com/transaction/${txid}`;
  return null;
};

const AutoDepositsTab = ({ token }) => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDeposits = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/mnro/auto-deposits?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeposits(res.data || []);
    } catch {
      toast.error("Ошибка загрузки авто-депозитов");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDeposits();
    const t = setInterval(() => fetchDeposits(true), 30000);
    return () => clearInterval(t);
  }, [token]); // eslint-disable-line

  const totalUsdt = deposits
    .filter((d) => d.asset_kind === "USDT")
    .reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const totalTrx = deposits
    .filter((d) => d.asset_kind === "TRX")
    .reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const totalTon = deposits
    .filter((d) => d.asset_kind === "TON")
    .reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const totalBnb = deposits
    .filter((d) => d.asset_kind === "BNB")
    .reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);

  return (
    <div data-testid="autodeposits-tab-content">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/15 border border-white/30 flex items-center justify-center">
            <Radio size={20} className="text-[#EDEDED]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Авто-депозиты</h2>
            <p className="text-sm text-[#A1A1AA]">
              Транзакции, обнаруженные мониторингом блокчейна (USDT TRC-20 + TRX + TON + BNB)
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchDeposits()}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-[#111114] hover:bg-[#1a1a1f] border border-[#1F1F1F] hover:border-[#2E2E2E] text-white text-sm rounded-lg transition-colors disabled:opacity-50"
          data-testid="autodeposits-refresh-btn"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Обновить
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-4">
          <div className="text-xs text-[#A1A1AA] mb-1">Всего записей</div>
          <div className="text-2xl font-semibold text-white" data-testid="autodeposits-count">
            {deposits.length}
          </div>
        </div>
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-4">
          <div className="text-xs text-[#A1A1AA] mb-1">Сумма USDT</div>
          <div className="text-2xl font-semibold text-[#26A17B]" data-testid="autodeposits-sum-usdt">
            {totalUsdt.toFixed(2)}
          </div>
        </div>
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-4">
          <div className="text-xs text-[#A1A1AA] mb-1">Сумма TRX</div>
          <div className="text-2xl font-semibold text-[#FF060A]" data-testid="autodeposits-sum-trx">
            {totalTrx.toFixed(4)}
          </div>
        </div>
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-4">
          <div className="text-xs text-[#A1A1AA] mb-1">Сумма TON</div>
          <div className="text-2xl font-semibold text-[#0098EA]" data-testid="autodeposits-sum-ton">
            {totalTon.toFixed(4)}
          </div>
        </div>
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-4">
          <div className="text-xs text-[#A1A1AA] mb-1">Сумма BNB</div>
          <div className="text-2xl font-semibold text-[#F0B90B]" data-testid="autodeposits-sum-bnb">
            {totalBnb.toFixed(6)}
          </div>
        </div>
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-4">
          <div className="text-xs text-[#A1A1AA] mb-1">Сети</div>
          <div className="text-2xl font-semibold text-white">
            {[...new Set(deposits.map((d) => d.network).filter(Boolean))].join(", ") || "—"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A1A1AA]">Загрузка…</div>
      ) : deposits.length === 0 ? (
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg p-12 text-center">
          <Radio size={32} className="mx-auto text-[#52525B] mb-3" />
          <div className="text-white font-medium mb-1">Авто-депозитов пока нет</div>
          <p className="text-sm text-[#A1A1AA]">
            Как только пользователь переведёт USDT на свой TRC-20 адрес, транзакция
            появится здесь автоматически.
          </p>
        </div>
      ) : (
        <div className="bg-[#111114] border border-[#1F1F1F] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1a1a1f] text-[#A1A1AA] text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Время</th>
                  <th className="text-left px-4 py-3 font-medium">Пользователь</th>
                  <th className="text-left px-4 py-3 font-medium">Сеть</th>
                  <th className="text-right px-4 py-3 font-medium">Сумма</th>
                  <th className="text-left px-4 py-3 font-medium">От</th>
                  <th className="text-left px-4 py-3 font-medium">На</th>
                  <th className="text-left px-4 py-3 font-medium">TxID</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((d) => {
                  const url = explorerUrl(d.network, d.txid);
                  return (
                    <tr
                      key={d.txid || d.id}
                      className="border-t border-[#1F1F1F] hover:bg-[#0d0d10]"
                      data-testid={`autodeposit-row-${d.txid}`}
                    >
                      <td className="px-4 py-3 text-[#A1A1AA] whitespace-nowrap">
                        {fmtTime(d.created_at)}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {d.user_email || d.user_id}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs border ${
                            networkColors[d.network] ||
                            "bg-[#1F1F1F] text-[#A1A1AA] border-[#3f3f46]"
                          }`}
                        >
                          {d.network || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                        <span className="text-[#EDEDED]">
                          +{(parseFloat(d.amount) || 0).toFixed(d.asset_kind === "USDT" ? 2 : (d.asset_kind === "BNB" ? 6 : 4))}
                        </span>
                        <span className="text-[#A1A1AA] text-xs ml-1">{d.asset_kind || ""}</span>
                      </td>
                      <td className="px-4 py-3 text-[#A1A1AA] font-mono text-xs">
                        {shorten(d.from_address)}
                      </td>
                      <td className="px-4 py-3 text-[#A1A1AA] font-mono text-xs">
                        {shorten(d.to_address)}
                      </td>
                      <td className="px-4 py-3">
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[#EDEDED] hover:underline font-mono text-xs"
                          >
                            {shorten(d.txid)}
                            <ExternalLink size={12} />
                          </a>
                        ) : (
                          <span className="text-[#A1A1AA] font-mono text-xs">
                            {shorten(d.txid)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoDepositsTab;
